import { useEffect, useState } from "react";
import Select from "react-select";
import useGroup from "../hook/useGroup";
import { getGroupsBySearch } from "../services/Group";
import Cookies from "js-cookie";

const PAGE_SIZE = 10;
const DEBOUNCE_DELAY = 500;

export const GroupSelect = () => {
    const token = Cookies.get("token-my-cdir");
    const { selectedGroupId, setSelectedGroupId, setSelectedTenantId, isSuperAdmin }: any = useGroup();

    const [options, setOptions] = useState<any[]>([]);
    const [page, setPage] = useState(0);

    const [search, setSearch] = useState("");
    const [inputValue, setInputValue] = useState("");

    const [hasMore, setHasMore] = useState(true);
    const [loading, setLoading] = useState(false);
    const [totalCount, setTotalCount] = useState(0);
    const [selectedOption, setSelectedOption] = useState<any>(null);

    const fetchGroups = async (searchText = "", pageNumber = 0, append = false, isReset = false) => {
        if (!isSuperAdmin) return;

        try {
            setLoading(true);

            const res = await getGroupsBySearch(
                searchText,
                pageNumber,
                PAGE_SIZE,
                token!.toString()
            );

            const formatted =
                res?.groupReports?.map((item: any) => ({
                    value: item.id,
                    label: item.name,
                    tenantId: item.tenantId,
                })) || [];

            setTotalCount(res?.count || 0);

            setOptions((prev) => {
                const updated = append
                    ? [...prev, ...formatted]
                    : formatted;

                setHasMore(
                    updated.length < (res?.count || 0)
                );

                // auto select first item
                if (
                    updated.length > 0 &&
                    !selectedGroupId && !isReset
                ) {
                    setSelectedOption(updated[0]);
                    setSelectedGroupId(updated[0].value);
                    setSelectedTenantId(updated[0].tenantId);
                }

                return updated;
            });
        } catch (error) {
            console.log(error);
        } finally {
            setLoading(false);
        }
    };

    // initial load
    useEffect(() => {
        if (isSuperAdmin) {
            fetchGroups("", 0);
        }

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isSuperAdmin]);

    // debounce search
    useEffect(() => {
        const timer = setTimeout(() => {
            setSearch(inputValue);
            setPage(0);

            fetchGroups(inputValue, 0, false);
        }, DEBOUNCE_DELAY);

        return () => clearTimeout(timer);

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [inputValue]);

    if (!isSuperAdmin) return null;

    return (
        <div className="my-auto flex flex-col md:flex-row-reverse gap-[0.75rem]">
            {selectedOption && (
                <div className="px-[0.75rem] py-[0.375rem] bg-blue-50 border border-blue-200 rounded-md max-w-full md:max-w-xs">
                    <span
                        className="font-medium text-blue-700 truncate block"
                        title={selectedOption?.label}
                    >
                        {selectedOption?.label}
                    </span>
                </div>
            )}

            <Select
                options={options}
                isLoading={loading}
                placeholder={`Search Church (${totalCount})`}
                className="w-full xm:w-64"
                isSearchable

                value={null}

                onChange={(selected: any) => {
                    // reset pagination/search 
                    setInputValue("");
                    setSearch("");
                    setPage(0);
                    setHasMore(true);
                    fetchGroups("", 0, false, true);

                    setSelectedOption(selected);
                    setSelectedGroupId(selected?.value);
                    setSelectedTenantId(selected?.tenantId);
                }}

                // typing
                onInputChange={(value, actionMeta) => {
                    if (
                        actionMeta.action !==
                        "input-change"
                    )
                        return;

                    setInputValue(value);

                    return value;
                }}

                // infinite scroll
                onMenuScrollToBottom={() => {
                    if (loading || !hasMore) return;

                    const nextPage = page + 1;

                    setPage(nextPage);

                    fetchGroups(
                        search,
                        nextPage,
                        true
                    );
                }}

                noOptionsMessage={() =>
                    loading
                        ? "Loading..."
                        : "No churches found"
                }
            />
        </div>
    );
};
